// scanVideoUpload.js — a clip from the phone to the job, in three steps.
// ---------------------------------------------------------------------------
// v0.207.0. Brett: "I have taken some test video for floor scan. I dint have a
// plan at the time. Can you add a spot where i can select the videos from my
// device and save it on the job for processing. Save me getting out a chord and
// transferring."
//
// ── WHY NOT THE WAY EVERY OTHER UPLOAD WORKS ──────────────────────────────
//
// Every other upload in DropPin reads the file into a base64 `data:` URL and
// posts it to the Function. That is right for a photo and IMPOSSIBLE for video:
// a two-minute 4K floor scan is hundreds of megabytes, base64 adds a third
// again, the browser holds the lot in memory, and Static Web Apps caps the
// request body long before any of that. It would fail on the phone, on site,
// with the footage already shot.
//
// So the bytes go STRAIGHT from the phone to storage:
//
//   1. ticket   POST /api/scan-videos     → a write-only SAS for one blob, and
//                                            a row marked 'pending'
//   2. put      PUT  <that url>           → the file itself, never through us
//   3. confirm  PATCH /api/scan-videos/id → 'ready'
//
// A clip that fails at step 2 leaves a row saying 'pending', and the list SAYS
// SO. That is deliberate: see shared/blob.js v0.130.0 on the four months when a
// failed upload returned the original data URL and every caller treated total
// failure as total success. A visibly broken save gets fixed; a silently
// corrupt one does not.
//
// TRANSPORT IS INJECTED so the whole sequence can be tested without a network:
// `post`, `put` and `patch` are supplied by the caller.

export const VIDEO_MIME = /^video\/(mp4|quicktime|x-m4v|webm|3gpp|x-matroska|mpeg|x-msvideo)\b/i;
export const MAX_BYTES = 4 * 1024 * 1024 * 1024;

/** Human size, for a message somebody reads while waiting on 4G. */
export function fmtBytes(n) {
  const b = Number(n);
  if (!Number.isFinite(b) || b <= 0) return '';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${Math.round(b / 1024)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / 1024 / 1024).toFixed(b < 10 * 1024 * 1024 ? 1 : 0)} MB`;
  return `${(b / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

/**
 * Is this file one we can take? Returns { ok } or { ok:false, reason }.
 *
 * The reason is written for somebody standing in a kitchen holding a phone,
 * not for a log: it says what to do next.
 */
export function checkVideoFile(file) {
  if (!file) return { ok: false, reason: 'No file was chosen.' };
  const mime = String(file.type || '');
  const size = Number(file.size || 0);
  // A phone occasionally hands over a clip with an EMPTY type. The name is the
  // only other evidence, and refusing a real video over a missing header would
  // send Brett back to the cable, which is the whole thing this replaces.
  const byName = /\.(mp4|mov|m4v|webm|3gp|mkv|avi|mpe?g)$/i.test(String(file.name || ''));
  if (!VIDEO_MIME.test(mime) && !byName) {
    return { ok: false, reason: `“${file.name || 'That file'}” is not a video this can store. Pick a clip from the camera roll.` };
  }
  if (size <= 0) {
    return { ok: false, reason: `“${file.name || 'That file'}” is empty — it may still be saving on the phone. Try again in a moment.` };
  }
  if (size > MAX_BYTES) {
    return { ok: false, reason: `“${file.name}” is ${fmtBytes(size)}, past the 4 GB limit. Trim it on the phone first.` };
  }
  return { ok: true, mime: VIDEO_MIME.test(mime) ? mime : mimeFromName(file.name) };
}

/** A type from the file name, for the phones that send none. */
export function mimeFromName(name) {
  const ext = String(name || '').toLowerCase().split('.').pop();
  const map = {
    mp4: 'video/mp4', m4v: 'video/x-m4v', mov: 'video/quicktime',
    webm: 'video/webm', '3gp': 'video/3gpp', mkv: 'video/x-matroska',
    avi: 'video/x-msvideo', mpg: 'video/mpeg', mpeg: 'video/mpeg',
  };
  return map[ext] || 'video/mp4';
}

/**
 * ONE clip, all three steps.
 *
 * @param {object} args
 * @param {File}   args.file
 * @param {number} args.projectId
 * @param {number} [args.reportId]
 * @param {function} args.post   (path, body) => response
 * @param {function} args.put    (url, file, {onProgress}) => void — throws on failure
 * @param {function} args.patch  (path, body) => response
 * @param {function} [args.onStage] (stage, info) — 'checking'|'ticket'|'uploading'|'confirming'|'done'
 */
export async function uploadScanVideo({ file, projectId, reportId, post, put, patch, onStage }) {
  const say = (stage, info) => { try { onStage && onStage(stage, info || {}); } catch (_) { /* a reporter must never break an upload */ } };

  say('checking');
  const check = checkVideoFile(file);
  if (!check.ok) return { ok: false, stage: 'checking', error: check.reason };

  say('ticket');
  let ticket;
  try {
    ticket = await post('/api/scan-videos', {
      projectId,
      reportId: reportId ?? null,
      mime: check.mime,
      originalName: file.name || null,
      name: stripExt(file.name),
      sizeBytes: file.size || null,
      // WHEN THE CAMERA RECORDED IT, which is the field that makes the footage
      // matchable to a pass on the plan later. A File carries lastModified and
      // for a camera roll clip that IS the recording time.
      recordedAt: file.lastModified ? new Date(file.lastModified).toISOString() : null,
    });
  } catch (e) {
    // The API writes a good sentence for this one, so it IS the message — with
    // its own `detail` beside it (api.js carries that off the response body).
    return { ok: false, stage: 'ticket', error: messageOf(e, 'Could not start the upload.'), detail: e && e.detail ? String(e.detail) : null };
  }
  // The offline outbox must never swallow this: a write ticket queued for
  // later is a ticket that has expired by the time it is sent.
  if (ticket && ticket.queued) {
    return { ok: false, stage: 'ticket', error: 'You are offline. A video needs a connection — it has not been saved, and it is still on your phone.' };
  }
  // v0.207.3 — ALREADY ON THE JOB. Brett picked his clips a second time after a
  // failure told him to, and got duplicates. The API now recognises a clip it
  // already holds and returns it INSTEAD of a ticket, so there is nothing to
  // upload: this is a success, and saying otherwise would have him pick them
  // yet again.
  if (ticket && ticket.duplicate) {
    return { ok: true, duplicate: true, video: ticket.video };
  }
  if (!ticket || !ticket.uploadUrl || !ticket.video || ticket.video.id == null) {
    return { ok: false, stage: 'ticket', error: messageOf(ticket, 'The upload could not be started.') };
  }

  say('uploading', { id: ticket.video.id, bytes: file.size || 0 });
  let putError = null;
  try {
    await put(ticket.uploadUrl, file, { onProgress: (pct) => say('uploading', { id: ticket.video.id, pct, bytes: file.size || 0 }) });
  } catch (e) {
    // v0.207.2 — DO NOT TAKE THE BROWSER'S WORD FOR IT.
    //
    // Brett: "videos say did not finish uploading - add it again. Even though i
    // saw it get to 100%". He was right to disbelieve it. The browser can only
    // see its own request, and there are several ways for a PUT to send every
    // byte and still hand back an error — a response the browser will not read
    // for want of a CORS header being the likeliest. Treating that as "your
    // footage did not arrive" told him to do the one thing he should not have
    // had to do, twice.
    //
    // So the failure is REMEMBERED, not announced, and we fall through to the
    // confirm — which asks STORAGE. If the bytes are there the clip is fine and
    // nobody needs to know the browser was confused. If they are not, the
    // confirm refuses and the message below is finally the truth.
    putError = e;
  }

  say('confirming', { id: ticket.video.id });
  let row;
  try {
    row = await patch(`/api/scan-videos/${ticket.video.id}`, { status: 'ready', sizeBytes: file.size || null });
  } catch (e) {
    // THE SERVER CHECKED STORAGE AND THE BLOB IS NOT THERE. Now — and only now
    // — the upload really did fail, whatever the progress bar said.
    if (e && e.blobMissing) {
      try { await patch(`/api/scan-videos/${ticket.video.id}`, { status: 'failed' }); } catch (_) { /* reported regardless */ }
      return {
        ok: false, stage: 'uploading', videoId: ticket.video.id,
        error: 'The video did not finish uploading. It is still on your phone — try again.',
        // The browser's own complaint, when it had one, is the useful half here.
        detail: messageOf(putError, null) || messageOf(e, null),
      };
    }
    // Anything else — storage unreachable, the API down — leaves the row
    // PENDING and leaves the bytes wherever they got to. Saying "it failed"
    // would be a guess, and the row can be confirmed again later without the
    // clip being uploaded a second time.
    return {
      ok: false, stage: 'confirming', videoId: ticket.video.id, uploaded: !putError,
      error: 'The video could not be confirmed, so it is showing as pending. Nothing has been lost — use Check storage on the clip to finish it.',
      detail: messageOf(e, null),
    };
  }
  say('done', { id: ticket.video.id });
  // The bytes landed even though the browser said otherwise. Worth returning so
  // a caller can say so rather than showing a clean save that contradicts what
  // the person just watched happen.
  return {
    ok: true,
    video: row || ticket.video,
    resumed: !!ticket.resumed,
    recoveredFrom: putError ? messageOf(putError, null) : null,
  };
}

function stripExt(name) {
  const s = String(name || '').trim();
  if (!s) return null;
  return s.replace(/\.[a-z0-9]{1,5}$/i, '') || null;
}

function messageOf(e, fallback) {
  if (!e) return fallback;
  if (typeof e === 'string') return e.trim() || fallback;
  const m = e.error || e.message;
  return (typeof m === 'string' && m.trim()) ? m.trim() : fallback;
}
